import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'
import logger from './logger'
import { parseRateLimitHeaders, setRateLimitCooldown, isEndpointRateLimited, getRemainingCooldown } from './rateLimitHandler'

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3001'
const ADMIN_API_URL = process.env.NEXT_PUBLIC_ADMIN_API_URL || 'http://localhost:3002'
const MOBILE_API_URL = process.env.NEXT_PUBLIC_MOBILE_API_URL || 'http://localhost:3003'

// Extend axios config to track request timing
interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
  metadata?: {
    startTime: number
  }
}

export const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Enable sending cookies with requests
})

export const adminApi = axios.create({
  baseURL: ADMIN_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Enable sending cookies with requests
})

export const mobileApi = axios.create({
  baseURL: MOBILE_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

export { MOBILE_API_URL }

// Request interceptor to add timing metadata and logging
// NOTE: Auth token is now handled via HttpOnly cookies, not Authorization header
const addTimingAndLog = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
  // Add timing metadata
  const extendedConfig = config as ExtendedAxiosRequestConfig
  extendedConfig.metadata = { startTime: Date.now() }

  // Log request
  logger.api.request(config.method || 'GET', config.url || '', {
    baseURL: config.baseURL,
  })

  return config
}

api.interceptors.request.use(addTimingAndLog)
adminApi.interceptors.request.use(addTimingAndLog)

// Response interceptor to handle token refresh and logging
const handleTokenRefresh = async (error: AxiosError, apiInstance: typeof api) => {
  const originalRequest = error.config as ExtendedAxiosRequestConfig & { _retry?: boolean }

  // Log error
  const duration = originalRequest?.metadata?.startTime
    ? Date.now() - originalRequest.metadata.startTime
    : undefined
  logger.api.error(
    originalRequest?.method || 'UNKNOWN',
    originalRequest?.url || '',
    error.response?.status,
    error
  )

  // Handle rate limiting
  if (error.response?.status === 429) {
    const rateLimitInfo = parseRateLimitHeaders(error)
    if (rateLimitInfo && originalRequest?.url) {
      setRateLimitCooldown(originalRequest.url, rateLimitInfo.retryAfter)
      logger.warn(`Rate limited on ${originalRequest.url}. Retry after ${rateLimitInfo.retryAfter}s`)
    }
    return Promise.reject(error)
  }

  if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
    originalRequest._retry = true

    try {
      logger.info('Attempting token refresh via cookie')
      // Refresh token is read from HttpOnly cookie by the backend
      const response = await axios.post(`${API_URL}/api/auth/refresh`, {}, {
        withCredentials: true // Ensure cookies are sent
      })

      logger.info('Token refresh successful - new token set in cookie')
      // Token is now in HttpOnly cookie, retry the original request
      return apiInstance(originalRequest)
    } catch (refreshError) {
      logger.error('Token refresh failed', refreshError)
      // Redirect to login - backend will clear cookies
      window.location.href = '/login'
      return Promise.reject(refreshError)
    }
  }

  return Promise.reject(error)
}

// Success response interceptor with logging
const handleSuccessResponse = (response: any) => {
  const config = response.config as ExtendedAxiosRequestConfig
  const duration = config?.metadata?.startTime
    ? Date.now() - config.metadata.startTime
    : undefined

  logger.api.response(
    config?.method || 'GET',
    config?.url || '',
    response.status,
    duration
  )

  return response
}

api.interceptors.response.use(
  handleSuccessResponse,
  (error) => handleTokenRefresh(error, api)
)

adminApi.interceptors.response.use(
  handleSuccessResponse,
  (error) => handleTokenRefresh(error, adminApi)
)

export default api
